import React from 'react';
import SpanTimeline, { type SpanWording } from 'src/shared/components/span-timeline/SpanTimeline';
import type { TimeRange } from 'src/shared/components/span-timeline/timeAxis';
import type { StacItem } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import type { ValiditySegment } from '../../domain/versions/versionTimeline';
import { versionName } from '../../domain/versions/versionName';

type VersionTimelineProps = {
    segments: ValiditySegment[];
    /** The whole axis: the span of the dataset's life on screen. */
    bounds: TimeRange;
    /** The version whose detail is on screen. */
    selectedId?: string;
    /** The instant the map is resolved against. */
    atInstant: Date;
    onAtChange: (at: Date) => void;
    onSelect: (version: StacItem) => void;
    /** Sits in the card's top corner, beside the timeline it changes. */
    controls?: React.ReactNode;
};

/**
 * The catalogue's own words for what a bar on the timeline means.
 *
 * A version *holds* from the instant it was computed until the next one was (ADR-016 decision
 * 2) — "in force" is the engine's own term for that, and the timeline should say what the
 * engine says.
 */
const CATALOGUE_WORDING: SpanWording = {
    held: 'in force',
    ongoing: 'now and still in force',
    beyondWindow: 'an instant beyond this window',
    carriedIn: 'computed before this window',
};

/**
 * How long each version of a dataset was the one in force.
 *
 * The drawing is a general one — spans on an axis with a playhead over them — so it lives in
 * `SpanTimeline` and knows nothing of STAC. This is the adapter: it turns validity segments into
 * spans, lends the timeline the catalogue's vocabulary, and hands a chosen version back to its
 * caller rather than a bare identifier.
 */
const VersionTimeline = ({ segments, bounds, selectedId, atInstant, onAtChange, onSelect, controls }: VersionTimelineProps) => (
    <SpanTimeline
        spans={segments.map(segment => ({
            id: segment.version.id,
            label: versionName(segment.version),
            from: segment.from,
            to: segment.to,
            carriedIn: segment.carriedIn,
            endsBeyondWindow: segment.endsBeyondWindow,
        }))}
        bounds={bounds}
        selectedId={selectedId}
        atInstant={atInstant}
        onAtChange={onAtChange}
        onSelect={id => {
            const chosen = segments.find(segment => segment.version.id === id);
            if (chosen) {
                onSelect(chosen.version);
            }
        }}
        controls={controls}
        wording={CATALOGUE_WORDING}
        testIdPrefix='timeline-version'
    />
);

export default VersionTimeline;
